import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import type { BizyPeopleBrowserEntry, BizyPeopleBrowserResponse, BizyPersonMomentsResponse } from '../lib/bizy/types';
import { bizyUnreadNotificationsQueryKey } from './bizy-notifications';
import { headers } from './meta';

// Member-facing People Browser data layer (BIZY-635), modeled on
// `src/queries/bizy-conversations.ts`. Endpoint contracts are FE-proposed on
// BIZY-632 and may shift as the backend lands.
//
// The shared `headers` const adds `Key-Inflection: camel`, so responses arrive
// camelCase (matching the `BizyPeople*` types).

// ─── Query Keys ──────────────────────────────────────────────────────────────

enum QueryKey {
	People = 'bizy-people',
	PersonMoments = 'bizy-person-moments',
}

export const bizyPeopleQueryKey = () => [QueryKey.People];

export const bizyPersonMomentsQueryKey = (personId?: string) => [QueryKey.PersonMoments, personId].filter(Boolean);

// ─── Path helpers ────────────────────────────────────────────────────────────

export const bizyPeoplePath = (prefix = '') => `${prefix}/bizy/people`;
export const bizyPersonMomentsPath = (personId: string, prefix = '') => `${prefix}/bizy/people/${personId}/moments`;
// Seen is keyed by the observed moment (BIZY-673): the backend opens the
// moment's notification row, creating a pre-opened one for moments that never
// had a row, so every sidebar entry can clear its unread dot. Skip remains a
// notification write (dismissed_at) keyed by `notificationId`.
export const bizyMomentSeenPath = (observedMomentId: string, prefix = '') => `${prefix}/bizy/people/moments/${observedMomentId}/seen`;
export const bizyNotificationSkipPath = (notificationId: string, prefix = '') => `${prefix}/bizy/notifications/${notificationId}/skip`;

// ─── API response types ──────────────────────────────────────────────────────

interface BizyMarkSeenResponse {
	notificationId: string;
	observedMomentId: string;
	openedAt: string;
}

interface BizySkipResponse {
	notificationId: string;
	dismissedAt: string;
}

// ─── Query hooks ─────────────────────────────────────────────────────────────

/**
 * Fetches the People Browser sidebar list: at most 5 entries, one per person,
 * sorted by each person's most recent observed moment.
 *
 * GET /api/v2/bizy/people
 */
export const useBizyPeopleBrowserEntries = (options?: { enabled?: boolean }) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: bizyPeopleQueryKey(),
		queryFn: async (): Promise<BizyPeopleBrowserEntry[]> => {
			const res = await http.get<BizyPeopleBrowserResponse>(bizyPeoplePath(), { headers });
			return res.data.people;
		},
		enabled: options?.enabled ?? true,
	});
};

/**
 * Fetches one person's full moment history for the detail card.
 *
 * GET /api/v2/bizy/people/:personId/moments
 */
export const usePersonMoments = (personId: string | undefined) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: bizyPersonMomentsQueryKey(personId),
		queryFn: async (): Promise<BizyPersonMomentsResponse> => {
			// personId is guaranteed by `enabled: !!personId` below
			const res = await http.get<BizyPersonMomentsResponse>(bizyPersonMomentsPath(personId as string), { headers });
			return res.data;
		},
		enabled: !!personId,
		// Override the global keepPreviousData: the previous person's moments are
		// never useful for the next card, and holding them suppresses the person
		// view's loading skeleton when paging (BIZY-665).
		keepPreviousData: false,
	});
};

// ─── Mutation hooks ──────────────────────────────────────────────────────────

/**
 * Marks a People Browser moment as seen/opened, keyed by the observed moment
 * (BIZY-673). The backend opens the moment's notification row, creating a
 * pre-opened one for moments that never had a row.
 *
 * POST /api/v2/bizy/people/moments/:observedMomentId/seen
 */
export const useBizyMarkMomentSeenMutation = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (observedMomentId: string): Promise<BizyMarkSeenResponse> => {
			const res = await http.post<BizyMarkSeenResponse>(bizyMomentSeenPath(observedMomentId), {}, { headers });
			return res.data;
		},
		onSuccess: () => {
			// Marking a moment seen changes unread state too — refresh the
			// navbar badge / peek source alongside the sidebar list.
			queryClient.invalidateQueries(bizyPeopleQueryKey());
			queryClient.invalidateQueries(bizyUnreadNotificationsQueryKey());
		},
	});
};

/**
 * Skips a People Browser entry (sets `dismissed_at`), removing it from the
 * sidebar.
 *
 * POST /api/v2/bizy/notifications/:notificationId/skip
 */
export const useBizySkipMomentMutation = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (notificationId: string): Promise<BizySkipResponse> => {
			const res = await http.post<BizySkipResponse>(bizyNotificationSkipPath(notificationId), {}, { headers });
			return res.data;
		},
		onSuccess: () => {
			// Skipping a moment removes it from the sidebar and clears its
			// unread — refresh both caches.
			queryClient.invalidateQueries(bizyPeopleQueryKey());
			queryClient.invalidateQueries(bizyUnreadNotificationsQueryKey());
		},
	});
};
